import { useCallback } from "react";
import { createShipment, bookPorter, deleteShipment } from "../shipmentService";
import { getCurrentUTCTimestamp } from "../../../utils/dateResolvers";
import services from "../../../services/orderServices";
import { DeliveryModes, DispatchMode } from "../../../types";
import { SelectedRiderDetails } from "../components/QuoteBar";

interface CreateShipmentOptions {
  orderNumbers: string[];
  orderIds: Record<string, string>;
  deliveryMode?: DeliveryModes;
  dispatchMode?: DispatchMode;
  userName?: string;
  skipDispatchTransition?: boolean;
  skipGeocodeValidation?: boolean;
}

interface DispatchOnlyOptions {
  orderNumbers: string[];
  orderIds: Record<string, string>;
  rider?: SelectedRiderDetails;
}

interface ShipmentCreationReturn {
  createShipmentForOrders: (options: CreateShipmentOptions) => Promise<any>;
  bookPorterForShipment: (shipmentId: string) => Promise<any>;
  deleteShipmentById: (shipmentId: string) => Promise<void>;
  createShipmentAndBookPorter: (
    orderNumbers: string[],
    orderIds: Record<string, string>,
    deliveryMode?: DeliveryModes,
    dispatchMode?: DispatchMode,
    maskDeliveryContactDetails?: boolean,
    userName?: string,
    skipDispatchTransition?: boolean
  ) => Promise<{ shipment: any; porter: any }>;
  dispatchOrdersOnly: (options: DispatchOnlyOptions) => Promise<void>;
}

const transitionOrdersToDispatched = async (
  orderNumbers: string[],
  orderIdMap: Record<string, string>
) => {
  try {
    const transitionPromises = orderNumbers.map(async (orderNo) => {
      try {
        const orderId = orderIdMap[orderNo];
        if (!orderId) {
          throw new Error(`Order ID not found for ${orderNo}`);
        }
        await services.transitionOrderStatus(orderId, "DISPATCH");
      } catch (error) {
        console.error(`✗ Failed to transition order ${orderNo}:`, error);
        throw error;
      }
    });

    await Promise.all(transitionPromises);
  } catch (error) {
    console.error("Failed to transition orders to DISPATCHED:", error);
    throw new Error(
      "Failed to update order status to DISPATCHED. Please try again."
    );
  }
};

const updateOrderDispatchTime = async (
  orderNumbers: string[],
  orderIdMap: Record<string, string>
): Promise<{ failedOrderNumbers: string[] }> => {
  const dispatchTime = getCurrentUTCTimestamp();
  const results = await Promise.allSettled(
    orderNumbers.map(async (orderNo) => {
      const orderId = orderIdMap[orderNo];
      if (!orderId) {
        throw new Error(`Order ID not found for ${orderNo}`);
      }
      await services.updateOrderMetaInfo(orderId, {
        dispatchTime: dispatchTime,
      });
    })
  );

  const failedOrderNumbers = results
    .map((result, index) => {
      if (result.status === "rejected") {
        console.error(
          `Failed to update dispatch time for order ${orderNumbers[index]}:`,
          result.reason
        );
        return orderNumbers[index];
      }
      return null;
    })
    .filter((orderNo): orderNo is string => orderNo !== null);

  return { failedOrderNumbers };
};

const saveDispatchRiderMeta = async (
  orderNumbers: string[],
  orderIdMap: Record<string, string>,
  rider?: SelectedRiderDetails
): Promise<{ failedOrderNumbers: string[] }> => {
  // Notification trigger requires riderId (USTAAD user id). Skip when missing.
  if (!rider?.id) {
    return { failedOrderNumbers: [] };
  }
  const riderMeta = {
    dispatchRider: {
      riderId: rider.id,
      name: rider.name || "",
      phone: rider.phone || "",
      vehicleNumber: rider.vehicleNumber || "",
      trackingUrl: rider.trackingUrl || "",
    },
  };
  const results = await Promise.allSettled(
    orderNumbers.map(async (orderNo) => {
      const orderId = orderIdMap[orderNo];
      if (!orderId) {
        throw new Error(`Order ID not found for ${orderNo}`);
      }
      await services.updateOrderMetaInfo(orderId, riderMeta);
    })
  );

  const failedOrderNumbers = results
    .map((result, index) => {
      if (result.status === "rejected") {
        console.error(
          `Failed to save dispatch rider meta for order ${orderNumbers[index]}:`,
          result.reason
        );
        return orderNumbers[index];
      }
      return null;
    })
    .filter((orderNo): orderNo is string => orderNo !== null);

  return { failedOrderNumbers };
};

export const useShipmentCreation = (): ShipmentCreationReturn => {
  const createShipmentForOrders = useCallback(
    async ({
      orderNumbers,
      orderIds,
      deliveryMode,
      dispatchMode,
      userName,
      skipDispatchTransition = false,
      skipGeocodeValidation,
    }: CreateShipmentOptions) => {
      try {
        if (!skipDispatchTransition) {
          await transitionOrdersToDispatched(orderNumbers, orderIds);
          await updateOrderDispatchTime(orderNumbers, orderIds);
        }

        const shipmentPayload = {
          orderNumbers,
          additionalComments: userName || "",
          deliveryMode,
          dispatchMode,
        };

        const response = await createShipment(
          shipmentPayload,
          skipGeocodeValidation
        );

        if (!response.success) {
          throw new Error(response.message || "Failed to create shipment");
        }

        return response.data;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.errorMsg ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to create shipment. Please try again.";

        throw new Error(errorMessage);
      }
    },
    []
  );

  const dispatchOrdersOnly = useCallback(
    async ({ orderNumbers, orderIds, rider }: DispatchOnlyOptions) => {
      await transitionOrdersToDispatched(orderNumbers, orderIds);
      const dispatchTimeResult = await updateOrderDispatchTime(
        orderNumbers,
        orderIds
      );
      const riderMetaResult = await saveDispatchRiderMeta(
        orderNumbers,
        orderIds,
        rider
      );

      const failedOrderNumbers = Array.from(
        new Set([
          ...dispatchTimeResult.failedOrderNumbers,
          ...riderMetaResult.failedOrderNumbers,
        ])
      );

      if (failedOrderNumbers.length > 0) {
        throw new Error(
          `Orders moved to Dispatch, but failed to save dispatch metadata for: ${failedOrderNumbers.join(
            ", "
          )}. Please retry for those orders.`
        );
      }
    },
    []
  );

  const bookPorterForShipment = useCallback(
    async (shipmentId: string, maskDeliveryContactDetails?: boolean) => {
      try {
        const porterBookingPayload = {
          shipmentId,
          maskDeliveryContactDetails,
        };

        const response = await bookPorter(porterBookingPayload);

        if (!response.request_id || !response.order_id) {
          throw new Error("Failed to book porter - unexpected response format");
        }

        return response;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.errorMsg ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to book porter";

        throw new Error(errorMessage);
      }
    },
    []
  );

  const deleteShipmentById = useCallback(async (shipmentId: string) => {
    try {
      await deleteShipment(shipmentId);
    } catch (error) {
      console.error("Failed to delete shipment:", error);
    }
  }, []);

  const createShipmentAndBookPorter = useCallback(
    async (
      orderNumbers: string[],
      orderIds: Record<string, string>,
      deliveryMode?: DeliveryModes,
      dispatchMode?: DispatchMode,
      maskDeliveryContactDetails?: boolean,
      userName?: string,
      skipDispatchTransition = false
    ) => {
      let shipmentData: any = null;

      try {
        shipmentData = await createShipmentForOrders({
          orderNumbers,
          orderIds,
          deliveryMode,
          dispatchMode,
          userName,
          skipDispatchTransition,
        });
        const porterResponse = await bookPorterForShipment(
          shipmentData.shipmentId,
          maskDeliveryContactDetails
        );
        return {
          shipment: shipmentData,
          porter: porterResponse,
        } as const;
      } catch (error) {
        if (shipmentData?.shipmentId) {
          await deleteShipmentById(shipmentData.shipmentId);
        }
        throw error;
      }
    },
    [createShipmentForOrders, bookPorterForShipment, deleteShipmentById]
  );

  return {
    createShipmentForOrders,
    bookPorterForShipment,
    deleteShipmentById,
    createShipmentAndBookPorter,
    dispatchOrdersOnly,
  };
};
